import { List, Stack, Grid, Box } from '@mui/material';
import { useTranslation } from 'react-i18next';
import { Role } from '@wavingroup/iot-core/gen/ts/wavin/ignite/v1/user_pb';
import { NavItem } from '~/pages/layout/components/NavItem';
import { NotificationsDrawer } from '~/pages/layout/components/NotificationDrawer/NotificationsDrawer';
import { useHasUnreadNotifications } from '~/pages/layout/hooks/useHasUnreadNotifications';
import {
  SignOutIcon,
  DevicesListIcon,
  FirmwareListIcon,
  GearIcon,
  UsersIcon,
  SupportIcon,
  DeviceStatesIcon,
} from '~/shared/components/Icons/Icons';
import { useCloseSession } from '~/shared/hooks/useCloseSession';
import { useIsSignedIn } from '~/shared/auth/auth-provider';
import { createRoutes } from '~/routes';
import { RoleModel } from '~/shared/models/role/RoleModel';
import { useUserHasRequiredRole } from '~/shared/hooks/useUser';

export function DrawerContent({
  onClose,
  isMobile,
}: {
  onClose: () => void;
  isMobile: boolean;
}) {
  const { t } = useTranslation();

  const isSignedIn = useIsSignedIn();
  const closeSession = useCloseSession();
  const isIAMAdmin = useUserHasRequiredRole(new RoleModel(Role.IAM_ADMIN));
  const isDeviceAdmin = useUserHasRequiredRole(
    new RoleModel(Role.DEVICE_ADMIN),
  );
  const isFirmwareViewer = useUserHasRequiredRole(
    new RoleModel(Role.FIRMWARE_VIEWER),
  );
  const { hasUnreadNotifications } = useHasUnreadNotifications();

  if (!isSignedIn) return null;

  return (
    <>
      <Grid>
        <Stack
          component={List}
          onClick={() => isMobile && onClose()}
          sx={{
            gap: 6,
          }}
        >
          <NotificationsDrawer hasNewNotifications={hasUnreadNotifications} />
          <NavItem
            text={t('navigation.systems')}
            path={createRoutes.Devices}
            icon={<DevicesListIcon />}
          />
          {isFirmwareViewer && (
            <NavItem
              text={t('navigation.firmware')}
              path={createRoutes.Firmware}
              icon={<FirmwareListIcon />}
            />
          )}
          {isDeviceAdmin && (
            <NavItem
              text={t('navigation.deviceStates')}
              path={createRoutes.DeviceStates}
              icon={<DeviceStatesIcon />}
            />
          )}
          {isIAMAdmin && (
            <NavItem
              text={t('navigation.users')}
              path={createRoutes.Users}
              icon={<UsersIcon />}
            />
          )}
          <NavItem
            text={t('navigation.support')}
            path={createRoutes.Support}
            icon={<SupportIcon />}
          />
        </Stack>
      </Grid>
      <List>
        <Grid
          container
          sx={{
            justifyContent: 'center',
            gap: 6,
          }}
        >
          <Box onClick={() => isMobile && onClose()}>
            <NavItem
              text={t('navigation.settings')}
              path={createRoutes.Settings}
              icon={<GearIcon />}
            />
          </Box>
          <NavItem
            text={t('navigation.signOut')}
            onClick={() => closeSession()}
            icon={<SignOutIcon />}
          />
        </Grid>
      </List>
    </>
  );
}
